import { Star } from "lucide-react";

export interface FirmRelationship {
  rating:              number | null;
  notes:               string | null;
  tier:                number | null;
  placement_count:     number;
  last_interaction_at: string | null;
  contact_preferences: string | null;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function StarRating({ rating }: { rating: number | null }) {
  if (!rating) return null;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={13}
          className={n <= rating ? "fill-amber-400 text-amber-400" : "text-gray-200"}
        />
      ))}
    </div>
  );
}

interface Props {
  relationship: FirmRelationship | null;
}

export default function FirmRelationshipCard({ relationship }: Props) {
  if (!relationship) return null;

  const { rating, notes, tier, placement_count, last_interaction_at, contact_preferences } = relationship;
  const hasNotes = notes && notes.length > 0;
  const autoExpand = hasNotes && notes.length < 200;

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-5 py-4">
      <div className="flex items-center gap-3 mb-3">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Firm Relationship</p>
        {tier && (
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded font-medium">
            Tier {tier}
          </span>
        )}
      </div>

      <div className="flex items-center gap-5 flex-wrap">
        {rating && (
          <div>
            <p className="text-xs text-gray-400 mb-1">Rating</p>
            <StarRating rating={rating} />
          </div>
        )}

        {placement_count > 0 && (
          <div>
            <p className="text-xs text-gray-400 mb-1">Placements</p>
            <p className="text-sm font-semibold text-gray-900">{placement_count}</p>
          </div>
        )}

        {last_interaction_at && (
          <div>
            <p className="text-xs text-gray-400 mb-1">Last Contact</p>
            <p className="text-sm text-gray-700">{relativeTime(last_interaction_at)}</p>
          </div>
        )}
      </div>

      {contact_preferences && (
        <p className="mt-3 text-xs text-gray-500 italic">{contact_preferences}</p>
      )}

      {hasNotes && (
        <details open={autoExpand} className="mt-3">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 select-none">
            Notes
          </summary>
          <p className="mt-1.5 text-sm text-gray-700 whitespace-pre-wrap">{notes}</p>
        </details>
      )}
    </div>
  );
}
